import { Box, Center, Container, Flex } from '@chakra-ui/react'
import { Spinner } from '@wanda-space/noelle'
import { PwaPrompts } from 'components/Pwa/PwaPrompts'
import { useAppSelector } from 'hooks/useAppSelector'
import { useAuth } from 'hooks/useAuth'
import React, { Suspense, useState } from 'react'

import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { Layer } from '../styles/variables'
import { AuthenticatedNav } from './AuthenticatedNav/AuthenticatedNav'
import { AuthenticatedNavMenu } from './AuthenticatedNav/AuthenticatedNavMenu'
import { UnAutheticatedNav } from './UnAuthenticatedNav/UnAutheticatedNav'
import { AccountMergeModal } from './account-merge/AccountMergeModal'

type Props = {
  children: JSX.Element
}
// Header height plus the status bar inset, defined in styles.css
const navHeight = 'var(--wanda-nav-offset)'
const footerHeight = '75px'

const usedVerticalSpace = `${footerHeight} + ${navHeight} + 30px + var(--wanda-safe-area-bottom)`
// dvh keeps the layout stable while mobile browsers grow and shrink their toolbars.
// The vh value stays as the fallback for browsers that do not know the unit.
const contentHeightStyles = {
  minHeight: `calc(100vh - ${usedVerticalSpace})`,
  '@supports (height: 100dvh)': {
    minHeight: `calc(100dvh - ${usedVerticalSpace})`,
  },
}

const Layout = ({ children }: Props) => {
  const { isMainNavFixed } = useAppSelector((state) => state.ui)
  const [displayExpandedMenu, setDisplayExpandedMenu] = useState<boolean>(false)
  const { data: featureFlags } = useFeatureFlags()
  const { isAuthenticated, isAuthenticating } = useAuth()
  const currentLocation = window.location.pathname
  const hasFooterAndHeader =
    currentLocation !== '/register' &&
    currentLocation !== '/login' &&
    currentLocation !== '/finish-logging-in'

  return (
    <Flex
      overflowX="hidden"
      minHeight="100%"
      height="100%"
      width="100%"
      alignItems="center"
      flexDirection="column"
      backgroundColor="white"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        width="100vw"
        background="white"
        position={isMainNavFixed ? 'fixed' : 'initial'}
        zIndex={displayExpandedMenu ? Layer.NavContentMenu : 99}
        fontWeight="500"
        height={navHeight}
        p={5}
        // Clear the status bar and the rounded corners of the display. The base is the
        // p={5} above, referenced as its theme token so it stays on the spacing scale.
        pt="calc(var(--chakra-space-5) + var(--wanda-safe-area-top))"
        pl="calc(var(--chakra-space-5) + var(--wanda-safe-area-left))"
        pr="calc(var(--chakra-space-5) + var(--wanda-safe-area-right))"
        m="0"
      >
        <Suspense fallback={null}>
          {!hasFooterAndHeader || isAuthenticating ? null : isAuthenticated ? (
            <AuthenticatedNav
              onToggleShowMenu={() => setDisplayExpandedMenu(!displayExpandedMenu)}
              isMenuOpen={displayExpandedMenu}
            />
          ) : (
            <UnAutheticatedNav />
          )}
        </Suspense>
      </Flex>
      {/* Container here should have maxWidth dynamic based on buy boxes vs other pages and remove NarrowContainer component? */}
      {isAuthenticating && currentLocation !== '/finish-logging-in' ? (
        <Center width="100%" height="100%">
          <Spinner />
        </Center>
      ) : (
        <Container
          className="layout-container"
          padding={0}
          centerContent
          maxWidth={['100vw', '100vw', '100vw', '1184px']}
          marginTop={isMainNavFixed ? 'calc(4.3rem + var(--wanda-safe-area-top))' : 0}
          sx={contentHeightStyles}
          // Required so that floating footer doesn't float over content when scrolling all the way down in page,
          // plus the home indicator area on phones without a home button, or the PWA banners when they show
          // (their height already includes that area)
          paddingBottom={`calc(${footerHeight} + max(var(--wanda-safe-area-bottom), var(--wanda-pwa-prompts-height)))`}
          // Grow with the content instead of shrinking to the viewport, otherwise the content
          // overflows past the bottom padding above and it reserves nothing
          flexShrink={0}
          position="relative"
        >
          {children}
        </Container>
      )}
      {featureFlags?.ENABLE_USERS_MERGE && isAuthenticated ? <AccountMergeModal /> : null}
      <AuthenticatedNavMenu
        show={displayExpandedMenu}
        onDismiss={() => setDisplayExpandedMenu(false)}
      />
      <PwaPrompts />
    </Flex>
  )
}

export { Layout }
